import { useMemo, useState } from 'react';
import {
  DOSSIER_SCENARIO_LABELS,
  DossierScenarioSchema,
  type DossierScenario,
} from '../../lib/api-types';
import type { DossierCreateInput } from '../../lib/api';
import { useMandants } from '../mandants/hooks';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { FieldHelp } from '../../components/ui/FieldHelp';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Stepper, type StepperStep } from '../../components/ui/Stepper';

const STEPS: StepperStep[] = [
  { id: 'mandant', label: 'Client / mandant' },
  { id: 'mission', label: 'Mission & objet' },
  { id: 'review', label: 'Vérification' },
];

/** Clearly fictitious demo objet (no official value, just a sample label). */
const DEMO_OBJET = 'Évaluation — Villa R+1, Ouagadougou — Ouaga 2000 (ex. démonstration)';
const DEMO_SCENARIO: DossierScenario = 'PV_RAPPORT';

/**
 * Guided, multi-step wrapper around the existing dossier-creation flow. It uses
 * the SAME `createDossier` mutation and idempotency-key pattern as the quick
 * form — it only groups the real, API-accepted fields (mandant, scenario,
 * objet) into clear steps with a progress indicator, prev/next and a review
 * step. No business rules live here; the server validates as before.
 */
export function DossierCreateWizard({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (input: DossierCreateInput) => void;
  onCancel: () => void;
  pending?: boolean;
}): JSX.Element {
  const mandantsQuery = useMandants({ page: 1, pageSize: 100 });
  const mandants = useMemo(() => mandantsQuery.data?.items ?? [], [mandantsQuery.data]);

  const [step, setStep] = useState(0);
  const [mandantId, setMandantId] = useState('');
  const [scenario, setScenario] = useState<DossierScenario>('PV_RAPPORT');
  const [objet, setObjet] = useState('');
  const [stepError, setStepError] = useState('');
  const [confirmDemo, setConfirmDemo] = useState(false);

  const selectedMandant = mandants.find((m) => m.id === mandantId);

  const goNext = () => {
    if (step === 0 && !mandantId) {
      setStepError('Veuillez choisir un mandant pour continuer.');
      return;
    }
    setStepError('');
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goPrev = () => {
    setStepError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  const applyDemo = () => {
    // Prefill coherent demo values client-side; the user can still edit them.
    setMandantId((prev) => prev || mandants[0]?.id || '');
    setScenario(DEMO_SCENARIO);
    setObjet(DEMO_OBJET);
    setStepError('');
  };

  const requestDemo = () => {
    // Only overwrite already-entered data after an explicit confirmation.
    const hasData = Boolean(mandantId) || objet.trim().length > 0 || scenario !== 'PV_RAPPORT';
    if (hasData) {
      setConfirmDemo(true);
      return;
    }
    applyDemo();
  };

  const submit = () => {
    onSubmit({ mandantId, scenario, objet: objet.trim() ? objet.trim() : undefined });
  };

  return (
    <div className="space-y-5">
      <Stepper steps={STEPS} current={step} />

      {mandantsQuery.isError ? (
        <Alert variant="error">Impossible de charger la liste des mandants.</Alert>
      ) : null}

      <div className="rounded-md border border-dashed border-brand-200 bg-brand-50/50 px-4 py-3">
        <p className="text-sm text-slate-700">
          Première fois ? Vous pouvez partir d’un exemple de démonstration, puis tout modifier.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2"
          onClick={requestDemo}
          disabled={pending || mandantsQuery.isLoading}
        >
          Utiliser un exemple de démonstration
        </Button>
      </div>

      {stepError ? <Alert variant="warning">{stepError}</Alert> : null}

      {step === 0 ? (
        <div>
          <Label htmlFor="wizard-mandantId">Mandant</Label>
          <Select
            id="wizard-mandantId"
            value={mandantId}
            aria-invalid={Boolean(stepError)}
            onChange={(e) => setMandantId(e.target.value)}
          >
            <option value="">— Choisir un mandant —</option>
            {mandants.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nom}
              </option>
            ))}
          </Select>
          <FieldHelp>
            Le mandant est le client qui commande l’évaluation (banque, assurance, particulier…). Il
            doit déjà exister dans « Mandants ».
          </FieldHelp>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="space-y-4">
          <div>
            <Label htmlFor="wizard-scenario">Scénario</Label>
            <Select
              id="wizard-scenario"
              value={scenario}
              onChange={(e) => setScenario(e.target.value as DossierScenario)}
            >
              {DossierScenarioSchema.options.map((s) => (
                <option key={s} value={s}>
                  {DOSSIER_SCENARIO_LABELS[s]}
                </option>
              ))}
            </Select>
            <FieldHelp>
              Choisissez les livrables attendus : un procès-verbal (PV), un rapport, ou les deux.
            </FieldHelp>
          </div>
          <div>
            <Label htmlFor="wizard-objet">Objet (facultatif)</Label>
            <Input
              id="wizard-objet"
              value={objet}
              placeholder="ex. : Évaluation d’une villa à usage résidentiel"
              onChange={(e) => setObjet(e.target.value)}
            />
            <FieldHelp example="Évaluation — Villa R+1, Ouagadougou — Ouaga 2000">
              Une courte description libre de la mission, utile pour retrouver le dossier.
            </FieldHelp>
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Vérifiez les informations avant de créer le dossier. Le numéro officiel sera attribué
            automatiquement par le serveur.
          </p>
          <dl className="grid gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Mandant</dt>
              <dd className="text-sm text-slate-800">{selectedMandant?.nom ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Scénario</dt>
              <dd className="text-sm text-slate-800">{DOSSIER_SCENARIO_LABELS[scenario]}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-400">Objet</dt>
              <dd className="text-sm text-slate-800">{objet.trim() ? objet.trim() : '—'}</dd>
            </div>
          </dl>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Annuler
        </Button>
        <div className="flex gap-2">
          {step > 0 ? (
            <Button type="button" variant="outline" onClick={goPrev} disabled={pending}>
              Précédent
            </Button>
          ) : null}
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={goNext} disabled={pending}>
              Suivant
            </Button>
          ) : (
            <Button type="button" onClick={submit} loading={pending}>
              Créer le dossier
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDemo}
        onClose={() => setConfirmDemo(false)}
        onConfirm={() => {
          applyDemo();
          setConfirmDemo(false);
        }}
        title="Remplacer par l’exemple de démonstration ?"
        description="Des informations ont déjà été saisies. Voulez-vous les remplacer par les valeurs d’exemple ? Vous pourrez encore les modifier ensuite."
        confirmLabel="Remplacer"
      />
    </div>
  );
}
